// Project Detail — Required audits. Resolves the tracker's "Project Type
// (forms/docs required)" labels to inspection templates and lets staff create
// each inspection with one click (ready to schedule). Unmatched labels get a
// template picker.
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ClipboardCheck, Loader2, Plus, ExternalLink, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { SectionCard } from "@/components/SectionCard";

const STATUS_TONE = { Draft: "#9A968F", Submitted: "#2563EB", Approved: "#3F7D3A", Rejected: "#B33A2E" };

export default function RequiredAuditsPanel({ project, canEdit }) {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(null);   // label being created
    const [picks, setPicks] = useState({});           // label → chosen template_id (for unmatched)

    const load = useCallback(async () => {
        try {
            const { data } = await api.get(`/projects/${project.id}/required-audits`);
            setData(data);
        } catch { setData(null); }
        setLoading(false);
    }, [project.id]);

    useEffect(() => { load(); }, [load]);

    async function create(audit) {
        const templateId = audit.template_id || picks[audit.label];
        if (!templateId) { toast.error("Choose a template first"); return; }
        if (!data.site_id) { toast.error("Link a site to this project first (Sites & Buildings)"); return; }
        setCreating(audit.label);
        try {
            const { data: inst } = await api.post("/forms/instances", {
                template_id: templateId, client_id: data.client_id,
                project_id: project.id, site_id: data.site_id,
                title: `${audit.label} — ${project.name}`,
            });
            toast.success(`${audit.label} created — ready to schedule`);
            await load();
            navigate(`/app/inspections/${inst.id}`);
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not create inspection"); }
        finally { setCreating(null); }
    }

    if (loading) return null;
    if (!data || !data.audits?.length) return null;

    return (
        <SectionCard testId="project-required-audits-panel" id={`audits-${project.id}`} icon={ClipboardCheck}
            title="Required audits" count={`${data.audits.length} from the tracker`}>
            <div className="space-y-2">
                {data.audits.map((a, i) => {
                    const has = a.instances?.length > 0;
                    return (
                        <div key={i} data-testid={`required-audit-${i}`} className="rounded-[10px] border p-3" style={{ borderColor: "#EFEAE3", background: "#FCFBF9" }}>
                            <div className="flex items-start justify-between gap-3 flex-wrap">
                                <div className="min-w-0">
                                    <div className="text-[12.5px] font-semibold text-[#1F1F1F] flex items-center gap-1.5">
                                        {a.label}
                                        {a.matched
                                            ? <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: "#EAF3E8", color: "#3F7D3A" }}><CheckCircle2 className="w-3 h-3" /> {a.template_name}</span>
                                            : <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: "#FCF6EA", color: "#C68A1C" }}><AlertTriangle className="w-3 h-3" /> no matching template</span>}
                                    </div>
                                    {has && (
                                        <div className="flex flex-wrap gap-1.5 mt-1.5">
                                            {a.instances.map((inst) => (
                                                <button key={inst.id} data-testid={`required-audit-instance-${inst.id}`} onClick={() => navigate(`/app/inspections/${inst.id}`)}
                                                    className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border bg-white hover:bg-[#F6F4F0]" style={{ borderColor: "#E5E0DA" }}>
                                                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: STATUS_TONE[inst.status] || "#9A968F" }} />
                                                    {inst.status}{inst.due_date ? ` · due ${String(inst.due_date).slice(0, 10)}` : ""}
                                                    <ExternalLink className="w-3 h-3" />
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                {canEdit && (
                                    <div className="flex items-center gap-2">
                                        {!a.matched && (
                                            <select data-testid={`required-audit-pick-${i}`} value={picks[a.label] || ""} onChange={(e) => setPicks((p) => ({ ...p, [a.label]: e.target.value }))}
                                                className="h-8 px-2 rounded-lg border bg-white text-[11.5px]" style={{ borderColor: "#E5E0DA" }}>
                                                <option value="">Pick a template…</option>
                                                {data.templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                                            </select>
                                        )}
                                        <button data-testid={`required-audit-create-${i}`} onClick={() => create(a)} disabled={creating === a.label || (!a.matched && !picks[a.label])}
                                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-[8px] text-[11.5px] font-semibold text-white disabled:opacity-50" style={{ background: "var(--brand-accent,#C75A1B)" }}>
                                            {creating === a.label ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} {has ? "New" : "Create"}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </SectionCard>
    );
}
